import store from '@/store'
import axios from '@/utils/axios-intents'
import axiosIntents from '@/utils/axios-intents'

import StoreCache from '@/utils/StoreCache'

// Persistent cache that survives page refreshes
const CACHE_STORAGE_KEY = 'ufb_cache_v1'

const createEmptyCache = () => ({
    ufb_plans: {
        uuids: {},
        lifetime: 5 * 60 * 1000, // 5 min
    },
    modems : {
        uuids: {},
        lifetime: 5 * 60 * 1000,// 5 min
    }
})

// Load cache from localStorage or create empty cache
const loadCacheFromStorage = () => {
    try {
        const stored = localStorage.getItem(CACHE_STORAGE_KEY)
        if (stored) {
            const parsed = JSON.parse(stored)
            // Reconstruct StoreCache objects from stored data
            const cache = createEmptyCache()

            // Restore UFB plans cache
            if (parsed.ufb_plans?.uuids) {
                Object.keys(parsed.ufb_plans.uuids).forEach(key => {
                    const storedCache = parsed.ufb_plans.uuids[key]
                    if (storedCache && storedCache.data && storedCache.loaded) {
                        const storeCache = new StoreCache(cache.ufb_plans.lifetime)
                        storeCache.data = storedCache.data
                        storeCache.loaded = new Date(storedCache.loaded)

                        // Only restore if not expired
                        if (!storeCache.expired()) {
                            cache.ufb_plans.uuids[key] = storeCache
                        }
                    }
                })
            }

            // Restore modems cache
            if (parsed.modems?.uuids) {
                Object.keys(parsed.modems.uuids).forEach(key => {
                    const storedCache = parsed.modems.uuids[key]
                    if (storedCache && storedCache.data && storedCache.loaded) {
                        const storeCache = new StoreCache(cache.modems.lifetime)
                        storeCache.data = storedCache.data
                        storeCache.loaded = new Date(storedCache.loaded)

                        // Only restore if not expired
                        if (!storeCache.expired()) {
                            cache.modems.uuids[key] = storeCache
                        }
                    }
                })
            }

            console.log('Cache restored from localStorage:', {
                ufb_plans: Object.keys(cache.ufb_plans.uuids).length,
                modems: Object.keys(cache.modems.uuids).length
            })

            return cache
        }
    } catch (error) {
        console.warn('Failed to load cache from localStorage:', error)
    }

    return createEmptyCache()
}

// Save cache to localStorage
const saveCacheToStorage = (cache) => {
    try {
        // Convert StoreCache objects to plain objects for storage
        const toStore = {
            ufb_plans: {
                uuids: {},
                lifetime: cache.ufb_plans.lifetime
            },
            modems: {
                uuids: {},
                lifetime: cache.modems.lifetime
            }
        }

        // Store UFB plans cache
        Object.keys(cache.ufb_plans.uuids).forEach(key => {
            const storeCache = cache.ufb_plans.uuids[key]
            if (storeCache && !storeCache.expired()) {
                toStore.ufb_plans.uuids[key] = {
                    data: storeCache.data,
                    loaded: storeCache.loaded
                }
            }
        })

        // Store modems cache
        Object.keys(cache.modems.uuids).forEach(key => {
            const storeCache = cache.modems.uuids[key]
            if (storeCache && !storeCache.expired()) {
                toStore.modems.uuids[key] = {
                    data: storeCache.data,
                    loaded: storeCache.loaded
                }
            }
        })

        localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(toStore))
    } catch (error) {
        console.warn('Failed to save cache to localStorage:', error)
    }
}

const cached = loadCacheFromStorage()

import dynStorage from '@/utils/storage-dynamic'

const UFB_SELECTED_PRODUCT = 'ufb-selected-product'

import { db } from '@/utils/firebase'

const unsubscribes = {}
const unsubscribeAll = () => {
    for (const uuid in unsubscribes) {
        unsubscribes[ uuid ]()
        delete unsubscribes[ uuid ]
    }
}

const ufbPayloadFromBroadbandFlow = ({ uuid, location, transfer, service, plan, with_wifi_router, rsf, contact_phone }) => {
    const auth_user_profile_creds = store.getters.auth_user_profile_creds

    const payload = {
        UUID: uuid,
        SPID: store.getters.app_spid,
        CustomerUUID: store.getters.current_account_uid,
        Address: {
            ...(location ? {
                City: location.City,
                Country: location.Country,
                Postcode: location.PostCode,
                Lines: location.Lines,
                LocationProvider: location.LocationProvider,
                LocationProviderIDX: location.LocationProviderIDX,
            } : {}),
            // PrequalUUID: service.ID,
            Email: auth_user_profile_creds.email,
            Name: [auth_user_profile_creds.first_name, auth_user_profile_creds.last_name].join(' ').trim(),
            Phone: auth_user_profile_creds.phone,
        },
        ExternalReference: {
            ...(transfer && transfer?.required ? {
                OrderType: "TransferPrimary",
                TransferFrom: {
                    Provider: transfer.provider.name,
                    AccountHolderName: transfer.account.holder,
                    AccountNumber: transfer.account.number,
                    ConnectionID: transfer.connection.ID,
                },
            } : plan ? {
                OrderType: plan.OrderType,
            } : {}),
            ...(service ? {
                ServiceType: "Unmanaged",
                ServiceAvailabilityUUID: service.ID,
            } : {}),
            ...(rsf ? {
                RSFDate: rsf.StartTimeNanos,
            } : {}),
            SiteContactName: [auth_user_profile_creds.first_name, auth_user_profile_creds.last_name].join(' ').trim(),
            SiteContactPhone: contact_phone || auth_user_profile_creds.phone,
            SiteContactEmail: auth_user_profile_creds.email,
            ...(location ? {
                LocationProvider: location.LocationProvider,
                LocationProviderIDX: location.LocationProviderIDX,
            } : {}),
        },
        Items: [],
    }

    if (plan) {
        payload.Items = [{
            ItemUUID: plan.ID,
            ProductAvailabilityUUID: plan.ID,
            SPID: store.getters.app_spid,
            Type: "TYPE_VIRTUAL",
            VirtualType: "VT_PLAN",
        }]

        if (with_wifi_router) {
            payload.Items.push({
                ItemUUID: plan.ID,
                ProductAvailabilityUUID: plan.ID,
                SPID: store.getters.app_spid,
                Type: "TYPE_PRODUCT",
                VirtualType: "VT_NONE"
            })
        }
    }

    return payload
}

const state = {
    ufb_products: [],
    ufb_product_uuid: null,
}

const getters = {
    // ufb_product: state => state.ufb_products.find(ufb_product => ufb_product.UUID == state.ufb_product_uuid), //Todo refactor the UFB 
    // ufb_products: state => state.ufb_products || [], //Todo refactor the UFB
}

const mutations = {
    saveUFBProducts(state, ufb_products) {
        state.ufb_products = ufb_products || []
    },

    selectUFBProduct(state, UUID) {
        state.ufb_product_uuid = UUID

        dynStorage.set(UFB_SELECTED_PRODUCT, state.ufb_product_uuid, false)
    },

    resetUFBProduct() {
        state.ufb_products = []
        state.ufb_product_uuid = null

        unsubscribeAll()
    },
}

const actions = {
    ufbSearchAddress({}, payload) {
        return axios.get('/ufb/searchAddress', { params: payload })
            .then(({data}) => Promise.resolve(data))
    },

    // ufbPlans({commit}, { provider, provider_idx, refresh }) {
    //     const uuid = `${ provider }|${ provider_idx }`

    //     if (!refresh && uuid in cached.ufb_plans.uuids && !cached.ufb_plans.uuids[uuid].expired()) {
    //         return Promise.resolve(cached.ufb_plans.uuids[uuid].cachedData())
    //     } else {
    //         const params = {
    //             LocationProvider: provider,
    //             LocationProviderIDX: provider_idx,
    //         }

    //         return axios.get('/ufb/plans', { params }).then(({data}) => {
    //             const plans = Array.isArray(data?.Plans)
    //                 ? data?.Plans
    //                 : []

    //             if (!(uuid in cached.ufb_plans.uuids)) {
    //                 cached.ufb_plans.uuids[uuid] = new StoreCache(cached.ufb_plans.lifetime)
    //             }
    //             cached.ufb_plans.uuids[uuid].fix(plans)

    //             return Promise.resolve(cached.ufb_plans.uuids[uuid].cachedData())
    //         })
    //     }
    // },

    ufbChangePlan({getters}, { UUID, ProductAvailabilityUUID }) {
        const payload = {
            UUID,
            ProductAvailabilityUUID,
            CustomerUUID: getters.current_account_uid,
        }

        return axios.post('/ufb/change-plan', payload)
    },

    ufbCart({commit}, broadband_flow) {
        return axios.post('/ufb/cart', ufbPayloadFromBroadbandFlow(broadband_flow))
    },

    ufbCheckout({getters, commit, dispatch}, broadband_flow) {
        // return axios.post('/ufb/checkout', ufbPayloadFromBroadbandFlow(broadband_flow)).then(({data}) => {
        //     return getters.is_checkout_payment_confirmation && data?.ClientSecret
        //         ? dispatch('stripePaymentConfirmation', {
        //             card_token: broadband_flow.payment_method?.cardId || getters.defaultPaymentCardId,
        //             client_secret: data.ClientSecret,
        //         }).then(() => {
        //             commit('resetBroadbandFlow')

        //             return Promise.resolve(data)
        //         }).catch(error => {
        //             error.srtripe_client_secret = data.ClientSecret

        //             return Promise.reject(error)
        //         })
        //         : Promise.resolve(data)
        // }).then(data => {
        //     return data?.OrderID
        //         ? dispatch('ufbCheckoutOrderStatus', data.OrderID)
        //         : Promise.resolve(data)
        // }).then(() => {
        //     const PrequalUUID = broadband_flow?.service?.ID // ufbPayloadFromBroadbandFlow(broadband_flow) => ExternalReference.ServiceAvailabilityUUID || Address.PrequalUUID
            
        //     return PrequalUUID
        //         ? dispatch('ufbCheckSubscribeProduct', PrequalUUID)
        //         : Promise.resolve()
        // })
        return Promise.resolve() //we will remove this functionality later
    },

    ufbCheckoutOrderStatus({getters, dispatch}, OrderID) {
        return axios.get(`/eshop/v2/customer/${ getters.current_account_uid }/order/${ OrderID }`).then(({data}) => {
            return data?.Status
                ? data.Status == 'COMPLETED' || data.Status == 'PROCESSING'
                    ? Promise.resolve(data)
                    : data.Status == 'FAILED'
                        ? Promise.reject({
                            message: data?.OrderNotes?.[0]?.Note || 'Order failed',
                            order_status: true
                        })
                        : new Promise((resolve, reject) => {
                            setTimeout(() => {
                                dispatch('ufbCheckoutOrderStatus', OrderID)
                                    .then(data => resolve(data))
                                    .catch(error => reject(error))
                            }, 250)
                        })
                : Promise.resolve(data)
        })
    },

    ufbSubscribeProducts({getters, commit}) {
        const uuid = getters.current_account_uid

        if (uuid) {
            if (!(uuid in unsubscribes)) {
                unsubscribeAll()

                return new Promise(resolve => {
                    unsubscribes[uuid] = db.collection(`/customers/${ uuid }/ufb_products`).onSnapshot(collection => {
                        const ufb_products = []
    
                        collection.forEach((doc) => {
                            const ufb_product = doc.data()
    
                            if (!ufb_product.DeletedAtNanos) {
                                ufb_products.push(ufb_product)
                            }
                        })
    
                        commit('saveUFBProducts', ufb_products)

                        resolve(getters.ufb_products)
                    })
                })
            }
        } else {
            return Promise.resolve([])
        }
    },

    ufbUnsubscribeProducts() {
        unsubscribeAll()
    },

    ufbOrderTimeSlots({getters}, { OrderUUID, VisitType, ProductUUID }) {
        const params = {
            VisitType,
            CustomerUUID: getters.current_account_uid,
            ProductUUID,
        }


        return axios.get(`/ufb/v1/order/${ OrderUUID }/time_slots`, { params })
                    .then(({data}) => Promise.resolve(data?.AvailableTimeSlots || []))
    },

    ufbBookOrderAppointment({getters}, { OrderUUID, ProductUUID, Appointments }) {
        const payload = {
            CustomerUUID: getters.current_account_uid,
            ProductUUID,
            Appointments,
        }

        return axios.post(`/ufb/v1/order/${ OrderUUID }/book`, payload)
            .then(({data}) => Promise.resolve(data))
    },

    async getUFBModems({commit, getters}, force_refresh = true) {

        if (!force_refresh &&
            Object.keys(cached.modems.uuids).length > 0 &&
            !Object.values(cached.modems.uuids).some(cache => cache.expired()) ) {

                return Promise.resolve(
                    Object.values(cached.modems.uuids)
                    .map(cache => cache.cachedData())
                    .filter(Boolean)
                )

        } else {

            const params = {
                "SearchOptions": {
                    "PageNumber": 1,
                    "PageSize": 10,
                    "SortBy": "Name",
                    "SortDesc": true,
                },
                "IncludeDeleted": false,
                "SPID": getters.app_spid,
                "FacetFilters": [{
                    "Name": "NetworkType",
                    "Values": ["UFB"] //"UFB" for UFB
                }]
            }

            try {
                const modem_data = await axiosIntents.post('eshop/eshop_item_advanced_search', params)

                if (modem_data?.data?.ItemVariants?.length > 0 && Array.isArray(modem_data.data.ItemVariants)) {

                    const ItemVariants = modem_data.data.ItemVariants
                    const modems = []

                    ItemVariants.forEach(itemVariant => {

                        const modem = {
                            UUID: itemVariant.UUID,
                            ItemVariantUUID: itemVariant.UUID,
                            ItemUUID: itemVariant.ItemUUID,
                            ItemImages: itemVariant.ItemImages || [],
                            Name: itemVariant.Name,
                            Price: null,
                            PhysicalType: "MODEM",
                            ServiceType: "UFB",
                            Description: itemVariant.Description ?? null,
                            Attributes: []
                        }

                        if (Array.isArray(itemVariant.ItemVariantAttributes)) {

                            itemVariant.ItemVariantAttributes.forEach(attr => {
                                const { Name, Value, ShowToCustomer } = attr

                                if (!Value || Value.trim() === '') return

                                if (Name === "Price" && !modem.Price) {
                                    modem.Price = Value
                                }

                                if (ShowToCustomer === true && Name !== "Price") {
                                    modem.Attributes.push({Name, Value})
                                }                               
                            })
                        }

                        modems.push(modem)
                    })

                    modems.forEach(modem => {
                        //update cache service
                        const uuid = `${ modem.UUID }|${ modem.ItemVariantUUID }`
                        if (!cached.modems.uuids[uuid]) {
                            cached.modems.uuids[uuid] = new StoreCache(cached.modems.lifetime)
                        }

                        cached.modems.uuids[uuid].fix(modem)
                    })

                    return Promise.resolve(
                        Object.values(cached.modems.uuids)
                            .map(cache => cache.cachedData())
                            .filter(Boolean)
                    )
                    
                } else {
                    console.log('no modem data')
                    return Promise.reject('No modem data')
                }
            } catch (error) {
                console.log(error)
                return Promise.reject(error)
            }
        }
    },

    async getUfbPlans({commit}, { customer_uuid, service_uuid, force_refresh }) {

        if (!force_refresh &&
            Object.keys(cached.ufb_plans.uuids).length > 0 &&
            !Object.values(cached.ufb_plans.uuids).some(cache => cache.expired()) ) {

                return Promise.resolve(
                    Object.values(cached.ufb_plans.uuids)
                    .map(cache => cache.cachedData())
                    .filter(Boolean)
                )

        } else {

            try {
                const ufb_plans_data = await axiosIntents.get(`ufb/v1/customer/${customer_uuid}/service/${service_uuid}/plans`)

                if (ufb_plans_data?.data?.BroadbandPlans?.Plans?.length > 0 && Array.isArray(ufb_plans_data.data.BroadbandPlans.Plans)) {
                    const BroadbandPlans = ufb_plans_data.data.BroadbandPlans.Plans
                    BroadbandPlans.forEach(BroadbandPlan => {
                        const uuid = `${ customer_uuid }|${ BroadbandPlan.ID }`

                        if (!cached.ufb_plans.uuids[uuid]) {
                            cached.ufb_plans.uuids[uuid] = new StoreCache(cached.ufb_plans.lifetime)
                        }

                        cached.ufb_plans.uuids[uuid].fix(BroadbandPlan)

                    })

                    return Promise.resolve(
                        Object.values(cached.ufb_plans.uuids)
                            .map(cache => cache.cachedData())
                            .filter(Boolean)
                    )

                } else {
                    console.log('no UFB Plans data')
                    return Promise.reject('No UFB Plans data')
                }

            } catch (error) {
                return Promise.reject(error)
            }
        }
    },
    ufbChangePlan({getters}, { service_uuid, ProductAvailabilityUUID, ChangeNow }) {
        const payload = {
            CustomerUUID: getters.current_account_uid,
            ProductAvailabilityUUID,
            ChangeNow: ChangeNow,
        }

        return axios.post(`/ufb/v1/service/${service_uuid}/change-plan`, payload)
    },
}

export default {
	state,
	getters,
	mutations,
	actions,
}